// One item of the main navigation, in the top navigation (desk) or the bottom bar (phone).
// A destination with a screen is a link; the current one carries aria-current="page". A destination without a
// screen is text with the dashed tag "folgt": React Aria renders a disabled link without href as a span, so the
// item is neither focusable nor a link to nowhere, and screen readers read "Vergleiche, folgt".

import { useMatchRoute } from "@tanstack/react-router";
import { Link } from "react-aria-components";
import { Glyph } from "../../components/Glyph";
import { useT } from "../../lib/i18n";
import { type NavItem } from "./navigation";
import styles from "./shell.module.css";

interface NavEntryProps {
  item: NavItem;
  variant: "top" | "bottom";
}

export function NavEntry({ item, variant }: NavEntryProps) {
  const t = useT();
  const matchRoute = useMatchRoute();
  const label = t(variant === "top" ? (item.deskLabel ?? item.label) : item.label);
  const glyph = variant === "bottom" ? <Glyph name={item.glyph} /> : null;

  if (item.to === undefined) {
    return (
      <Link isDisabled className={variant === "top" ? styles.soonTop : styles.soonBottom}>
        {glyph}
        <span>{label}</span>
        <span className="sf-tag sf-tag--sample">{t("shell.soon")}</span>
      </Link>
    );
  }

  const current = matchRoute({ to: item.to, fuzzy: item.to !== "/" }) !== false;
  return (
    <Link href={item.to} aria-current={current ? "page" : undefined}>
      {glyph}
      {label}
    </Link>
  );
}
